import MemoCard from "./Model/MemoCard";
import arrayShuffle from 'array-shuffle';
import {ScoreDataTime} from "../Shared/Components/Score";
import {Quiz} from "../Shared/Model/QuizData";
import {Question, Settings} from "../Shared/Model/GameData";

export interface MemoState {
    cards: MemoCard[],
    time: number,
}

export default class MemoManager {
    private quiz: Quiz
    private settings: Settings
    onUpdate: () => void

    private readonly cards: MemoCard[]
    private selected: number[] = []
    private discovered: number[] = []
    private isCorrect: boolean = false

    private time: number = 0
    private interval = null

    constructor(quiz: Quiz, settings: Settings) {
        this.quiz = quiz
        this.settings = settings
        this.cards = this.createCards()
    }

    destroy() : void {
        this.interval && clearInterval(this.interval)
    }

    select(index: number) : void {
        this.startTimerIfNeeded()
        if (this.cards[index].state === "disabled") {
            return
        }
        this.handleSelect(index)
        this.check()
        this.onUpdate()
    }

    private handleSelect(index: number) : void {
        if (this.selected.includes(index)) {
            this.selected = []
        } else {
            if (this.selected.length === 2) {
                this.selected = []
            }
            this.selected.push(index)
        }
    }

    next(): boolean {
        this.selected = []
        this.check()
        return this.isFinished() === false
    }

    state(): MemoState {
        this.updateCards()
        return {
            cards: this.cards,
            time: this.time,
        }
    }

    currentQuestion(): Question | undefined {
        return this.isCorrect ? this.cards[this.selected[0]].question : undefined
    }

    hint() : string {
        return this.discovered.length > 0 ? "" : this.settings.explanation;
    }

    private createCards() : MemoCard[] {
        const cards = []
        const randomQuestions = arrayShuffle(this.quiz.questions).slice(0, 6)
        randomQuestions.forEach((q, i) => {
            cards.push(new MemoCard(i, q))
            cards.push(new MemoCard(i, q))
        })
        return arrayShuffle(cards)
    }

    private check() : void {
        this.isCorrect = false
        if (this.selected.length === 2) {
            const c1 = this.cards[this.selected[0]]
            const c2 = this.cards[this.selected[1]]
            if (c1.id === c2.id) {
                this.isCorrect = true
                this.discovered = this.discovered.concat(this.selected)
            }
        }
    }

    private updateCards() : void {
        this.cards.forEach((c, i) => {
            if (this.selected.includes(i)) {
                c.state = "visible"
            } else if (this.discovered.includes(i)) {
                c.state = "disabled"
            } else {
                c.state = "reversed"
            }
        })
    }

    private tick() : void {
        if (this.isCorrect === false && this.isFinished() === false) {
            this.time++
            this.onUpdate()
        }
    }

    score(): ScoreDataTime {
        return {
            type: "time",
            discovered: this.discovered.length / 2,
            total: this.quiz.questions.length,
            time: this.time,
        }
    }

    private startTimerIfNeeded() : void {
        if (this.interval === null) {
            this.interval = setInterval(() => this.tick(), 1000)
        }
    }

    shouldDisplayNext() : boolean {
        return this.isCorrect || this.isFinished()
    }

    private isFinished() : boolean {
        return this.discovered.length === this.cards.length
    }
}